import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  deriveScrollbarSize,
  GRID_CELL_HEIGHT,
} from "@jana-technology/platform-ui";
import { debounce, throttle } from "lodash-es";

export const useInfiniteTableScroll = <
  T extends HTMLElement = HTMLDivElement,
  I extends HTMLElement = HTMLTableRowElement,
>(
  fetcher: () => void,
  options: Partial<Parameters<typeof useVirtualizer<T, I>>[0]>,
  threshold = 0.55,
  throttleMs = 111,
) => {
  const scrollRef = useRef<T>(null);

  const [{ paddingStart, paddingEnd }, setPadding] = useState({
    paddingStart: 0,
    paddingEnd: 0,
  });

  const {
    count = 0,
    estimateSize = () => GRID_CELL_HEIGHT,
    getScrollElement = () => scrollRef.current,
    ...optionalOptions
  } = options;

  const virtualizer = useVirtualizer<T, I>({
    count,
    estimateSize,
    getScrollElement,
    overscan: 10,
    paddingStart,
    paddingEnd,
    ...optionalOptions,
  });

  // account for table header height and horizontal scroll bars
  useLayoutEffect(() => {
    const { scrollElement } = virtualizer;

    if (scrollElement) {
      const tableElement = scrollElement.firstElementChild as HTMLTableElement;
      const { tHead } = tableElement;
      const hasHorizontalOverflow =
        scrollElement.scrollWidth > scrollElement.clientWidth;

      // reduce render cycles by approximating scroll bar height to 8px, if the offsetHeight is not set yet
      setPadding({
        paddingStart: tHead?.clientHeight ?? 0,
        paddingEnd: hasHorizontalOverflow
          ? deriveScrollbarSize(
              scrollElement.offsetHeight - scrollElement.clientHeight || 8,
            )
          : 0,
      });

      return () => {
        setPadding({
          paddingStart: 0,
          paddingEnd: 0,
        });
      };
    }
  }, [virtualizer]);

  useEffect(() => {
    const { scrollElement } = virtualizer;

    if (!scrollElement || !fetcher) {
      return;
    }

    const getMaxScrollTop = (div: HTMLDivElement | null) =>
      div
        ? Math.max(div.scrollHeight, virtualizer.getTotalSize()) -
          div.clientHeight
        : 0;

    const skipScrollFetch = (maxScrollTop: number) => maxScrollTop <= 0;

    const throttleConfig = {
      leading: true,
      trailing: true,
    };

    const handleScroll = throttle(
      (event: Event) => {
        const container = event.currentTarget as HTMLDivElement;

        const maxScrollTop = getMaxScrollTop(container);

        if (skipScrollFetch(maxScrollTop)) {
          return;
        }

        const ratio = container.scrollTop / maxScrollTop;

        if (ratio >= threshold) {
          fetcher();
        }
      },
      throttleMs,
      throttleConfig,
    );

    const handleScrollEnd = debounce(
      (event: Event) => {
        const container = event.currentTarget as HTMLDivElement;

        const maxScrollTop = getMaxScrollTop(container);

        if (skipScrollFetch(maxScrollTop)) {
          return;
        }

        const ratio = container.scrollTop / maxScrollTop;

        if (ratio >= threshold) {
          fetcher();
        }
      },
      throttleMs * 2,
      throttleConfig,
    );

    scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    scrollElement.addEventListener("scrollend", handleScrollEnd, {
      passive: true,
    });

    // if the table height is too small to scroll, we should still attempt to fetch more rows
    if (scrollElement.clientHeight / scrollElement.scrollHeight >= 0.9) {
      fetcher();
    }

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll);
      scrollElement.removeEventListener("scrollend", handleScrollEnd);

      handleScroll.cancel();
      handleScrollEnd.cancel();
    };
  }, [fetcher, threshold, throttleMs, virtualizer]);

  return useMemo(() => ({ virtualizer, scrollRef }), [virtualizer]);
};
